import React, { useEffect, useState, useCallback } from "react";
import { Chart } from "chart.js";
import axios from "axios";
import { Line } from "react-chartjs-2";
import { motion, AnimatePresence } from "framer-motion";
import "chart.js/auto";
import { buildApiUrl } from "../../lib/api";
import "./Balance.css";

function formatSourceLabel(source, accountSource) {
    if (source === "broker") return "Broker account";
    if (source === "trader") return "Trader account";
    if (source === "broker_unavailable") return "Broker unavailable";
    return accountSource === "broker" ? "Broker mode" : "Trader mode";
}

const Balance = ({ accountSource = "trader", revealed = false }) => {
    const [balanceData, setBalanceData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [currentBalance, setCurrentBalance] = useState(0);
    const [changeToday, setChangeToday] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selectedTimeframe, setSelectedTimeframe] = useState("ALL");
    const [hoveredBalance, setHoveredBalance] = useState(null);
    const [balanceSource, setBalanceSource] = useState("fallback");
    const [chartAnimKey, setChartAnimKey] = useState(0);
    const [chartAnimDuration, setChartAnimDuration] = useState(0);

    useEffect(() => {
        if (!revealed) return;
        const id = setTimeout(() => {
            setChartAnimDuration(900);
            setChartAnimKey((k) => k + 1);
        }, 180);
        return () => clearTimeout(id);
    }, [revealed]);

    const [accountHistories, setAccountHistories] = useState({
        daily: [],
        weekly: [],
        monthly: [],
        yearly: []
    });

    const verticalLinePlugin = {
        id: "verticalLine",
        beforeDraw: (chart) => {
            if (chart.tooltip?._active?.length) {
                const ctx = chart.ctx;
                const activePoint = chart.tooltip._active[0];
                const x = activePoint.element.x;
                const yAxis = chart.scales.y;

                ctx.save();
                ctx.beginPath();
                ctx.moveTo(x, yAxis.top);
                ctx.lineTo(x, yAxis.bottom);
                ctx.lineWidth = 1;
                ctx.strokeStyle = "#999";
                ctx.stroke();
                ctx.restore();
            }
        }
    };
    Chart.register(verticalLinePlugin);

    const fetchBalanceData = useCallback(async () => {
        setLoading(true);
        setHoveredBalance(null);
        try {
            const [historyResponse, currentResponse] = await Promise.all([
                axios.get(buildApiUrl("/balance/history"), {
                    params: { account_source: accountSource },
                    withCredentials: true,
                }),
                axios.get(buildApiUrl("/balance/current"), {
                    params: { account_source: accountSource },
                    withCredentials: true,
                }),
            ]);

            if (historyResponse.data) {
                const histories = {
                    daily:   historyResponse.data.daily   || [],
                    weekly:  historyResponse.data.weekly  || [],
                    monthly: historyResponse.data.monthly || [],
                    yearly:  historyResponse.data.yearly  || [],
                };
                setBalanceSource(historyResponse.data.source || "fallback");
                setAccountHistories(histories);
                const timeframeMapping = {
                    "1D": "daily",
                    "1W": "weekly",
                    "1M": "monthly",
                    "3M": "monthly",
                    "YTD": "yearly",
                    "1Y": "yearly",
                    "ALL": "yearly"
                };
                const selectedKey = timeframeMapping[selectedTimeframe] || "yearly";
                const selectedData = histories[selectedKey] || [];
                setBalanceData(histories.yearly);
                setFilteredData(selectedData);
            }

            if (currentResponse.data?.length > 0) {
                const snapshot = currentResponse.data[0];
                setBalanceSource(snapshot?.source || historyResponse.data?.source || "fallback");
                if (snapshot?.source === "broker_unavailable") {
                    setCurrentBalance(0);
                    setChangeToday(0);
                    setHoveredBalance(null);
                } else {
                    setCurrentBalance(snapshot.portfolio_value);
                    setChangeToday(snapshot?.profit_loss ?? 0);
                }
            } else {
                setCurrentBalance(0);
                setChangeToday(0);
                setHoveredBalance(null);
            }
        } catch (error) {
            console.error("Error fetching balance data:", error);
            setCurrentBalance(0);
            setChangeToday(0);
            setHoveredBalance(null);
            setFilteredData([]);
            setBalanceData([]);
        } finally {
            setLoading(false);
        }
    }, [accountSource, selectedTimeframe]);

    useEffect(() => {
        fetchBalanceData();
    }, [fetchBalanceData]);

    const handleTimeframeChange = (timeframe) => {
        setSelectedTimeframe(timeframe);
        setHoveredBalance(null);

        if (!accountHistories) {
            console.warn("accountHistories is undefined or empty.");
            setFilteredData([]);
            return;
        }

        const timeframeMapping = {
            "1D": "daily",
            "1W": "weekly",
            "1M": "monthly",
            "3M": "monthly",
            "YTD": "yearly",
            "1Y": "yearly",
            "ALL": "yearly"
        };

        const selectedKey = timeframeMapping[timeframe] || "yearly";
        const selectedData = accountHistories[selectedKey] || [];

        setFilteredData(selectedData);
    };

    const chartData = {
        labels: filteredData.map((entry) => entry.datetime),
        datasets: [
            {
                data: filteredData.map((entry) => entry.equity),
                borderColor: "rgba(0, 255, 255, 0.8)",
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return "rgba(0, 255, 255, 0.1)";
                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, "rgba(0, 255, 255, 0.22)");
                    gradient.addColorStop(1, "rgba(0, 255, 255, 0.0)");
                    return gradient;
                },
                fill: true,
                tension: 0.4,
                borderWidth: 2,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: chartAnimDuration,
            easing: "easeInOutQuart",
            delay: (ctx) => {
                if (ctx.type === "data" && ctx.mode === "default") {
                    return ctx.dataIndex * (600 / Math.max(filteredData.length, 1));
                }
                return 0;
            },
        },
        scales: {
            x: { display: false, grid: { display: false } },
            y: { display: false, grid: { display: false } }
        },
        elements: {
            line: { borderWidth: 2 },
            point: { radius: 0, hitRadius: 10, hoverRadius: 6 },
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: true,
                mode: "index",
                intersect: false,
                titleFont: {
                    family: "'Space Grotesk', sans-serif",
                    size: 14,
                    weight: "lighter",
                },
                callbacks: {
                    title: function (tooltipItems) {
                        const date = new Date(tooltipItems[0].label);

                        if (selectedTimeframe === "1D" || selectedTimeframe === "1W") {
                            return date.toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                                hour: "numeric",
                                minute: "numeric",
                                hour12: true
                            });
                        } else {
                            return date.toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric"
                            });
                        }
                    },
                    label: function (tooltipItem) {
                        setHoveredBalance(tooltipItem.raw);
                        return "";
                    }
                }
            }
        },
        hover: {
            mode: "index",
            intersect: false,
            onHover: (_, elements) => {
                if (elements.length === 0) {
                    setHoveredBalance(null);
                }
            },
            interaction: {
                mode: "index",
                intersect: false,
                axis: "x"
            },
        }
    };

    return (
        <div className="balance-chart-container">
            {loading ? (
                <p className="balance-loading">Loading...</p>
            ) : (
                <>
                    <div className="balance-header-row">
                        <span className="balance-section-label">Portfolio Performance</span>
                        <div className="balance-header-meta">
                            <span className={`balance-source-badge balance-source-badge--${balanceSource}`}>
                                {formatSourceLabel(balanceSource, accountSource)}
                            </span>
                            <span className={`balance-pnl-label ${changeToday >= 0 ? "positive" : "negative"}`}>
                                {typeof changeToday === "number" && !isNaN(changeToday)
                                    ? `${changeToday >= 0 ? "+" : ""}$${Number(changeToday).toFixed(2)}`
                                    : ""}
                            </span>
                        </div>
                    </div>
                    <h2 className="balance-title">
                        ${Number(hoveredBalance ?? currentBalance ?? 0).toFixed(2)}
                    </h2>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={selectedTimeframe}
                            className="chart"
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{ duration: 0.25, ease: "easeInOut" }}
                        >
                            <Line key={chartAnimKey} data={chartData} options={chartOptions} />
                        </motion.div>
                    </AnimatePresence>
                    <div className="timeframe-buttons">
                        {["1D", "1W", "1M", "3M", "YTD", "1Y", "ALL"].map((timeframe) => (
                            <button
                                key={timeframe}
                                className={selectedTimeframe === timeframe ? "active" : ""}
                                onClick={() => handleTimeframeChange(timeframe)}
                            >
                                {timeframe}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default Balance;
